import React from 'react';
import ProgressBar from './ProgressBar';
import UnifiedOrchestrationFlowChart from './UnifiedOrchestrationFlowChart';
import {
  getOrchestrationMessageText,
  shouldShowStopButton,
  shouldShowProcessingId
} from '../utils/orchestrationConfigs';
import { useWorkflowMetadataSync } from '../hooks/useWorkflowMetadata';
import './Chat.css'; // Use existing CSS

/**
 * UnifiedOrchestrationMessage component for rendering orchestration messages
 * 
 * This component replaces the separate fast-track and salesforce-orchestration
 * rendering logic with a unified, configurable approach.
 *
 * @param {Object} props - Component props
 * @param {Object} props.message - The message object
 * @param {Object} props.config - The orchestration configuration
 * @param {boolean} props.isCollapsed - Whether the orchestration details are collapsed
 * @param {Function} props.onToggleCollapse - Function to toggle collapse state
 * @param {Function} props.onStop - Function to stop the orchestration
 * @param {Function} props.getCurrentStepDisplay - Function to get step display name
 * @param {Function} props.getMessageHeader - Function to get message header for completed state
 */
const UnifiedOrchestrationMessage = ({
  message,
  config,
  isCollapsed,
  onToggleCollapse,
  onStop,
  getCurrentStepDisplay,
  getMessageHeader
}) => {
  // Get workflow metadata for enhanced functionality (must be at top level)
  const { metadata: workflowMetadata, workflowType, isShellWorkflow } = useWorkflowMetadataSync(message);

  if (!message || !config) return null;

  const status = message[config.statusField];
  const messageText = getOrchestrationMessageText(status, config, getCurrentStepDisplay);

  // For completed state, use the original getMessageHeader function if available
  const displayText = status?.status === 'completed' && getMessageHeader ?
    getMessageHeader(message) : messageText;



  const showStopButton = config.hasStopButton && shouldShowStopButton(status);
  const showProcessingId = shouldShowProcessingId(status);

  // Determine CSS classes based on message action
  const isFastTrackType = message.action === 'fast-track' || message.action === 'fast-track-shell';
  const containerClass = `${isFastTrackType ? 'fast-track' : 'salesforce-orchestration'}-message`;
  const headerClass = `${isFastTrackType ? 'fast-track' : 'salesforce-orchestration'}-header`;
  const actionsClass = `${isFastTrackType ? 'fast-track' : 'salesforce-orchestration'}-actions`;

  return (
    <div className="message-text">
      <div className={containerClass}>
        <div className={headerClass}>
          <span>{displayText}</span>
          <div className={actionsClass}>
            {/* Stop button */}
            {showStopButton && (
              <button
                className="stop-fast-track" // Reuse existing CSS class
                onClick={() => onStop(message.chatId, message.id)}
              >
                {config.buttons.stop}
              </button>
            )}
            
            {/* Collapse/Expand button */}
            <button
              className={message.action === 'fast-track' ? 'collapse-fast-track' : 'toggle-collapse'}
              onClick={() => onToggleCollapse(message.id)}
              title={message.action === 'fast-track' ?
                (isCollapsed ? config.buttons.collapse.expand : config.buttons.collapse.collapse) :
                undefined
              }
            >
              {config.icons.expandMore && config.icons.expandLess ? (
                <span className={config.icons.collapse}>
                  {isCollapsed ? config.icons.expandMore : config.icons.expandLess}
                </span>
              ) : (
                isCollapsed ? config.buttons.collapse.show : config.buttons.collapse.hide
              )}
            </button>
          </div>
        </div>

        {/* Orchestration Details */}
        {!isCollapsed && (
          <>
            {!status ? (
              // Initializing state
              <div className="initializing-message">
                <p>{message.content}</p>
                <div className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            ) : (
              // Progress and flow chart
              <>


                <ProgressBar
                  percentage={status.progress_percentage || 0}
                  currentStep={status.current_step || 'initializing'}
                  steps={config.steps}
                  stepLabels={config.stepLabels}
                  orchestrationData={status}
                  message={message}
                />
                <UnifiedOrchestrationFlowChart
                  orchestrationData={status}
                  config={config}
                  message={message}
                />
              </>
            )}
          </>
        )}

        {/* Processing ID */}
        {showProcessingId && (
          <div className="processing-id">
            Processing ID: {message.chatId}_{message.messageId || message.id}
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedOrchestrationMessage;
